
// region JS Imports

// region External Modules
import Promise from "plugins/bluebird"

import I18n from "plugins/i18n-js"
// endregion External Modules

// region Private Modules

import {
  q_ajax_3,
  ERROR_HANDLER_ACTIONS as AJAX_ERROR_HANDLER_ACTIONS,
} from "app/utils/q_ajax/q_ajax_3"

import {
  captureMessage,
} from "app/error_reporting"
// endregion Private Modules

// region Public Components
// endregion Public Components

// region Private Components
// endregion Private Components

// endregion JS Imports



import {
  ListingSearchDataSourceLevelTwoIdentifier,
} from "app/resources/listing_search/data_source_level_two_identifier/enum"

interface ListingSearchAttributes {
  show_extended_target_record?: boolean,
  [key: string]: any,
}

export interface Inputs {
  user_authentication_handle: string,
  listing_search_attributes: ListingSearchAttributes,
  saving_when_only_default_criteria_detected_allowed?: boolean,
  source_identifier: string,
  reject_promotions?: boolean | null,

  listing_search_data_source_level_two_identifier?: ListingSearchDataSourceLevelTwoIdentifier,
}

/* eslint-disable no-unused-vars */
enum ResultTypeEnums {
  SUCCESS = "SUCCESS",
  UNAUTHENTICATED = "UNAUTHENTICATED",
  RECORD_WITH_SAME_CRITERIA_EXIST = "RECORD_WITH_SAME_CRITERIA_EXIST",
  ONLY_DEFAULT_CRITERIA_DETECTED = "ONLY_DEFAULT_CRITERIA_DETECTED",
  INVALID = "INVALID",
  SERVER_ERROR = "SERVER_ERROR",
}
/* eslint-enable no-unused-vars */

type PromiseResolvePayload = {
  result_type: ResultTypeEnums.SUCCESS,
  data: {
    listing_search_url: string,
    listing_search: ListingSearchAttributes,
  },
  http_response_status_code: number,
} | {
  result_type: ResultTypeEnums.RECORD_WITH_SAME_CRITERIA_EXIST,
  http_response_status_code: number,
  data: {
    listing_search_url: string,
  }
} | {
  result_type:
    ResultTypeEnums.ONLY_DEFAULT_CRITERIA_DETECTED |
    ResultTypeEnums.UNAUTHENTICATED |
    ResultTypeEnums.SERVER_ERROR,
  http_response_status_code: number,
  data: null,
} | {
  result_type: ResultTypeEnums.INVALID,
  http_response_status_code: number | null,
  data: null,
}

const create = ({
  user_authentication_handle,
  listing_search_attributes,

  source_identifier,

  saving_when_only_default_criteria_detected_allowed,
  reject_promotions,

  listing_search_data_source_level_two_identifier,
}: Inputs): Promise<PromiseResolvePayload> => {
  if (listing_search_attributes == null) {

    captureMessage(
      `listing_search_attributes is null when trying to create listing_search from <${source_identifier}>`,
      {
        user_authentication_handle: user_authentication_handle,
        listing_search_attributes:  listing_search_attributes,

        source_identifier:          source_identifier,

        saving_when_only_default_criteria_detected_allowed:
          saving_when_only_default_criteria_detected_allowed,
      },
    )

    return Promise.resolve(({
      result_type:                ResultTypeEnums.INVALID,
      http_response_status_code:  null,
      data:                       null,
    }))
  }

  const request_inputs: {
    [key: string]: string | boolean | null | undefined | {
      [key: string]: string | boolean,
    },
  } = {
    user_authentication_handle:   user_authentication_handle,
    listing_search_attributes:    listing_search_attributes,
    locale:                       I18n.locale,
    show_extended_target_record:  listing_search_attributes.show_extended_target_record || false,

    reject_promotions:
      reject_promotions,
    saving_when_only_default_criteria_detected_allowed:
      saving_when_only_default_criteria_detected_allowed || false,
  }
  if (listing_search_data_source_level_two_identifier != null) {
    request_inputs.listing_search_data_source_level_two_identifier =
      listing_search_data_source_level_two_identifier
  }

  return q_ajax_3(
    {
      url:      "/api/web_app/listing_searches",
      type:     "POST",

      // Expect JSON in response
      dataType: "JSON",

      // Sending JSON to server
      contentType:  "application/json; charset=utf-8",
      data:         JSON.stringify(request_inputs),
    },
    {
      custom_error_handler: ({http_status_code}) => {
        if (http_status_code === 401) {
          return AJAX_ERROR_HANDLER_ACTIONS.STOP_RETRY
        }
        if (http_status_code === 409) {
          return AJAX_ERROR_HANDLER_ACTIONS.STOP_RETRY
        }
        if (http_status_code === 422) {
          return AJAX_ERROR_HANDLER_ACTIONS.STOP_RETRY
        }

        return AJAX_ERROR_HANDLER_ACTIONS.CONTINUE_RETRY
      },
    },
  )
  .then(({data, response}) => {
    return (
      {
        result_type:                ResultTypeEnums.SUCCESS,
        http_response_status_code:  response.status,

        data: {
          listing_search_url: data.data.listing_search_url,
          listing_search:     {
            id: data.data.listing_search.id,
          },
        },
      }
    )
  })
  .catch((reason_or_error) => {
    if (reason_or_error instanceof Error) {
      throw reason_or_error
    }

    const { response } = reason_or_error
    switch (response.status) {
      case 500:
      case 502:
      case 503:
      case 504:
        return ({
          result_type:                ResultTypeEnums.SERVER_ERROR,
          http_response_status_code:  response.status,
          data:                       null,
        })
      case 401:
        return ({
          result_type:                ResultTypeEnums.UNAUTHENTICATED,
          http_response_status_code:  response.status,
          data:                       null,
        })
      case 409:
        return response.json()
        .then((responseJSON: any) => {
          return ({
            result_type:                ResultTypeEnums.RECORD_WITH_SAME_CRITERIA_EXIST,
            http_response_status_code:  response.status,

            data: {
              listing_search_url: responseJSON.data.listing_search_url,
            },
          })
        })
      case 422: {
        return response.json()
        .then((responseJSON: any) => {
          if (responseJSON?.data?.error_type === "only_default_criteria_detected") {
            return ({
              result_type:                ResultTypeEnums.ONLY_DEFAULT_CRITERIA_DETECTED,
              http_response_status_code:  response.status,
              data:                       null,
            })
          }

          return ({
            result_type:                ResultTypeEnums.INVALID,
            http_response_status_code:  response.status,
            data:                       null,
          })
        })
      }
      default:
        return ({
          result_type:                ResultTypeEnums.INVALID,
          http_response_status_code:  response.status,
          data:                       null,
        })
    }
  })
}

export {
  create as default,
  create,
  ResultTypeEnums,
  ListingSearchDataSourceLevelTwoIdentifier,
}
